/**
 * AppSidebar — Role-filtered navigation sidebar with grouped sections
 *
 * Role hierarchy (each level includes all levels below):
 *   admin  →  sees everything
 *   delegate  →  sees delegate + secretary + common
 *   secretary  →  sees secretary + common
 *   user  →  sees common only
 */

import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Trophy,
  Users,
  Building2,
  Settings,
  ChevronLeft,
  ChevronRight,
  User,
  UserPlus,
  MapPin,
  ClipboardList,
  Shuffle,
  Timer,
  HelpCircle,
  Shield,
  ListChecks,
} from 'lucide-react';
import { useAuthStore } from '../../state_management_and_stores/auth.store';
import { cn } from '../../lib/utils';

interface SidebarItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  allowedRoles?: string[];
}

interface SidebarSection {
  label?: string;
  allowedRoles?: string[];
  items: SidebarItem[];
}

const sidebarSections: SidebarSection[] = [
  // Common — all authenticated users
  {
    items: [
      { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
      { label: 'Rodeos', icon: MapPin, path: '/rodeos' },
      { label: 'Jackpots', icon: Trophy, path: '/jackpots' },
      { label: 'Standings', icon: Trophy, path: '/standings' },
      { label: 'My Buddy Groups', icon: Users, path: '/my/buddy-groups' },
    ],
  },
  // Secretary & above — admin, delegate, secretary
  {
    label: 'Event Operations',
    allowedRoles: ['admin', 'delegate', 'secretary'],
    items: [
      { label: 'Draws', icon: Shuffle, path: '/delegate/draws' },
      { label: 'Live Scoring', icon: Timer, path: '/delegate/live-scoring' },
      // Surfaces contestant payment records — gated to admin+delegate to match
      // its route guard, so a secretary is never shown a link to a 403.
      {
        label: 'Prize Management',
        icon: Trophy,
        path: '/delegate/prize-management',
        allowedRoles: ['admin', 'delegate'],
      },
    ],
  },
  // Delegate & above — admin, delegate
  {
    label: 'Delegate',
    allowedRoles: ['admin', 'delegate'],
    items: [
      { label: 'My Jackpots', icon: ClipboardList, path: '/delegate/jackpots' },
      { label: 'My Rodeos', icon: MapPin, path: '/delegate/rodeos' },
      { label: 'Entries Ledger', icon: ListChecks, path: '/admin/entries-ledger' },
      { label: 'Season Roster', icon: Trophy, path: '/delegate/season-roster' },
    ],
  },
  // Admin only
  {
    label: 'Administration',
    allowedRoles: ['admin'],
    items: [
      { label: 'Admin Dashboard', icon: Shield, path: '/admin' },
      { label: 'Users', icon: Users, path: '/users' },
      { label: 'Bulk Entry', icon: UserPlus, path: '/admin/bulk-registration' },
      { label: 'Ferris Wheel', icon: ListChecks, path: '/admin/priorities' },
      { label: 'Organizations', icon: Building2, path: '/organizations' },
      { label: 'Settings', icon: Settings, path: '/settings' },
    ],
  },
  // Support — all authenticated users
  {
    items: [
      { label: 'Support', icon: HelpCircle, path: '/support' },
    ],
  },
];

interface AppSidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  /**
   * Called whenever a navigation link is selected. The mobile drawer passes
   * its close handler here so picking a menu item also collapses the overlay.
   * Omitted (no-op) for the persistent desktop sidebar.
   */
  onNavigate?: () => void;
}

export function AppSidebar({ collapsed, onToggleCollapse, onNavigate }: AppSidebarProps) {
  const { role } = useAuthStore();
  const userRoleName = role?.role_name;

  const visibleSections = sidebarSections
    .map((section) => {
      // If the section itself has role gating, check first
      if (section.allowedRoles && !section.allowedRoles.some((r) => r === userRoleName)) {
        return null;
      }

      // Filter individual items if they have per-item role gating
      const visibleItems = section.items.filter((item) => {
        if (!item.allowedRoles) return true;
        return item.allowedRoles.some((r) => r === userRoleName);
      });

      if (visibleItems.length === 0) return null;

      return { ...section, items: visibleItems };
    })
    .filter(Boolean) as SidebarSection[];

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center justify-center py-4 px-3 border-b border-sidebar-border">
        <NavLink to="/dashboard" onClick={onNavigate}>
          <>
            {/* Light mode: dark icon on light sidebar */}
            <img
              src="/icons/HighCallRodeo-icon-red.png"
              alt="HighCall"
              className={`w-auto dark:hidden ${collapsed ? 'h-10' : 'h-[50px]'}`}
            />
            {/* Dark mode: white icon on dark sidebar */}
            <img
              src="/icons/HighCallRodeo-icon-white.png"
              alt="HighCall"
              className={`drop-shadow-md w-auto hidden dark:block ${collapsed ? 'h-10' : 'h-[50px]'}`}
            />
          </>
        </NavLink>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto pt-3 pb-3 px-2">
        {visibleSections.map((section, sIdx) => (
          <div key={sIdx}>
            {/* Section divider (skip for first section) */}
            {sIdx > 0 && (
              <div className="my-2 mx-1 border-t border-sidebar-border" />
            )}

            {/* Section label */}
            {section.label && !collapsed && (
              <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-sidebar-foreground/40">
                {section.label}
              </div>
            )}

            <ul className="space-y-0.5">
              {section.items.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    end={!item.path.includes('/my/')}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none no-underline',
                        isActive
                          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                          : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/20 hover:text-sidebar-foreground',
                        collapsed && 'justify-center px-2',
                      )
                    }
                    title={collapsed ? item.label : undefined}
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Profile link + collapse toggle */}
      <div className="border-t border-sidebar-border p-2 space-y-1">
        <NavLink
          to="/profile"
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none no-underline',
              isActive
                ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/20 hover:text-sidebar-foreground',
              collapsed && 'justify-center px-2',
            )
          }
          title={collapsed ? 'Profile' : undefined}
        >
          <User className="h-4 w-4 shrink-0" />
          {!collapsed && <span className="truncate">Profile</span>}
        </NavLink>

        <button
          onClick={onToggleCollapse}
          className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-sidebar-foreground/70 hover:bg-sidebar-accent/20 hover:text-sidebar-foreground focus-visible:outline-none transition-colors w-full"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4 mx-auto" />
          ) : (
            <>
              <ChevronLeft className="h-4 w-4 shrink-0" />
              <span className="truncate">Collapse</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
